/**
 * @file LeadFormTrigger.tsx
 * @description Universal trigger button that opens a modal with LeadForm.
 *              Use instead of inline LeadForm when space is limited or
 *              form should not be visible until user initiates contact.
 * @usage <LeadFormTrigger subject="Оптовые условия" triggerLabel="Узнать условия" />
 */

"use client";

import { useState, type ReactNode } from "react";
import LeadFormModal from "@/components/LeadFormModal";
import { trackGoal } from "@/lib/metrika";

interface LeadFormTriggerProps {
  subject?: string;
  /**
   * Stable kebab-case channel label, forwarded unchanged to LeadForm.
   * See the `source vs subject contract` @rule at the top of LeadForm.tsx
   * for the full definition — in short: `source` is the grep/goal key,
   * `subject` is the human-readable Telegram detail line.
   *
   * @rule Deliberately has NO default. An absent source must reach LeadForm
   *       as undefined so its legacy fallback `source ?? subject ?? "LeadForm"`
   *       keeps working for callers that genuinely have no channel label.
   */
  source?: string;
  ctaLabel?: string;
  triggerLabel?: string;
  triggerIcon?: ReactNode;
  triggerVariant?: "primary" | "outline" | "on-primary" | "on-primary-soft";
  size?: "sm" | "md";
  modalTitle?: string;
  className?: string;
  /**
   * Goal fired the moment the modal OPENS — not when a lead is sent.
   *
   * @rule OPENING IS NOT A LEAD, AND THE TWO MUST NOT SHARE AN ID. This goal
   *       answers «сколько человек вообще заинтересовались», the submit goal
   *       answers «сколько дошли». A trigger without this prop fires nothing
   *       extra, so every existing caller keeps its exact two goals.
   * @rule Fired once per opening, including re-openings — a visitor who opens
   *       the form twice was interested twice. Deduplicating here would hide
   *       exactly the hesitation this number is meant to show.
   *       Introduced by series home-stage-c, step C05.
   */
  openGoal?: string;
  /**
   * Placeholder of the form's free-text field, forwarded to LeadFormModal →
   * LeadForm. Absent = the form's own default. Series inspection-cases, S3.
   */
  commentPlaceholder?: string;
}

export default function LeadFormTrigger({
  subject,
  source,
  ctaLabel = "Оставить заявку",
  triggerLabel = "Оставить заявку",
  triggerIcon,
  triggerVariant = "outline",
  size = "md",
  modalTitle,
  className,
  openGoal,
  commentPlaceholder,
}: LeadFormTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // @rule triggerVariant MUST match the surrounding background:
  //   - "primary"    → button has bg-primary fill. Use on light/neutral backgrounds.
  //   - "outline"    → primary border + primary text on transparent. Use on light backgrounds ONLY.
  //                    Never use on bg-primary sections — text-primary on bg-primary is invisible.
  //   - "on-primary" → white fill + primary text. Use on bg-primary (or any coloured) section.
  //   - "on-primary-soft" → translucent white fill (bg-white/20) + white text on bg-primary (or any coloured) section. Use as SECONDARY affordance alongside a primary CTA — softer visual weight than "on-primary". Includes flex/items/justify/gap utilities so an icon may be rendered inside the button (e.g. <FileText/> + label).
  // @rule size axis is orthogonal to triggerVariant for the three base variants
  //   (primary / outline / on-primary):
  //   - "md" (default) → px-6 py-3, default text size — for standalone CTAs
  //     (sidebar, hero, full-width sections).
  //   - "sm" → px-4 py-2 text-sm — for compact contexts (cards, inline,
  //     small surfaces). Used by NoscutCard.
  //   "on-primary-soft" IGNORES size — its responsive class already encodes
  //   compact-on-mobile / standard-on-desktop. Passing size="sm" with
  //   on-primary-soft has no effect.
  //   YAGNI: do not add "lg" or other size values until a real consumer
  //   requires them. Adding a size pre-emptively expands surface area.
  // @rule When adding a new variant, extend the switch below AND the triggerVariant union type.
  //       The exhaustiveness check (_exhaustive: never) will break the build if a case is missed.
  // Size-dependent classes for primary / outline / on-primary variants.
  // `on-primary-soft` ignores size — its responsive sm:px-8/py-4/text-base built-in already.
  const sizeCls = size === "sm" ? "px-4 py-2 text-sm" : "px-6 py-3";
  let btnCls: string;
  switch (triggerVariant) {
    case "primary":
      btnCls = `cursor-pointer w-full rounded-xl bg-primary font-medium text-white transition-colors hover:bg-primary/90 ${sizeCls}`;
      break;
    case "outline":
      btnCls = `cursor-pointer w-full rounded-xl border border-primary font-medium text-primary transition-colors hover:bg-primary hover:text-white ${sizeCls}`;
      break;
    case "on-primary":
      btnCls = `cursor-pointer w-full rounded-xl bg-white font-medium text-primary transition-colors hover:bg-white/90 ${sizeCls}`;
      break;
    case "on-primary-soft":
      btnCls = "cursor-pointer flex w-full items-center justify-center gap-2 rounded-xl bg-white/20 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/30 sm:px-8 sm:py-4 sm:text-base";
      break;
    default: {
      const _exhaustive: never = triggerVariant;
      throw new Error(`Unhandled triggerVariant: ${_exhaustive}`);
    }
  }

  const title = modalTitle || subject;

  return (
    <div className={className}>
      <button
        onClick={() => {
          if (openGoal) trackGoal(openGoal);
          setIsOpen(true);
        }}
        className={btnCls}
      >
        {triggerIcon ? (
          <span className="inline-flex items-center gap-2">
            {triggerIcon}
            {triggerLabel}
          </span>
        ) : (
          triggerLabel
        )}
      </button>

      <LeadFormModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={title}
        subject={subject}
        source={source}
        ctaLabel={ctaLabel}
        commentPlaceholder={commentPlaceholder}
      />
    </div>
  );
}
